// ─── SPECIES LORE ───────────────────────────────────────────────────────────
//
// The species_lore table has 11 rows and columns literally named `hierarchy`,
// `social_rules`, `concealment_rules`, `abilities`, `weaknesses`, `origin` and
// `transformation`. Nothing in the codebase has ever read it.
//
// The cost of that, from live play (2026-07-29): Kya asked a vampire Elder
// "and which rules never to break", and he answered "never break a blood oath,
// never betray a PACK BOND, and never touch another Elder's claimed partner."
// Pack bonds are werewolf lore — Werewolf is its own row in this very table —
// and none of the three matched the canon sitting in the database:
//
//   Vampire.social_rules: "Feeding etiquette is strict in courts — no killing
//   humans (in most modern courts), use of willing donors, no creating new
//   vampires without sire approval. Public exposure of vampire nature is
//   grounds for execution by court enforcers in most traditions."
//
// With no canon in the prompt the model confabulated, and borrowed vocabulary
// from the werewolf-heavy Discord voice samples that retrieval had supplied.
// That is the failure this module closes: voice samples teach a character how
// to SOUND, and were being asked to carry what the world IS.

import { supabase } from './supabaseClient.js';
import { speciesLabelOf, assertionIndex } from './speciesTraits.js';

let cache = null;
let cacheTime = 0;
const CACHE_TTL = 10 * 60 * 1000;

/**
 * Drop the cached species rows so the next prompt reloads them.
 *
 * Same failure as character overrides (Haley, 2026-08-11): the species editor
 * wrote to the database and nothing told this module, so edited world rules
 * took up to TEN minutes to reach a character — longer than the profile cache,
 * and species lore is exactly the kind of rule she edits because a character
 * got it wrong a moment earlier.
 */
export function invalidateSpeciesLore() {
  cache = null;
  cacheTime = 0;
}

/** Load every species row once and keep it briefly — 11 rows, rarely edited. */
async function loadAll() {
  const now = Date.now();
  if (cache && (now - cacheTime) < CACHE_TTL) return cache;
  try {
    // Every lore column — the 2026-08-03 editor writes 13 fields the old
    // 9-column select silently never surfaced (exceptions among them, which
    // is the one that produced Lance's fabricated ritual when it was missing).
    const { data, error } = await supabase
      .from('species_lore')
      .select('*');
    if (error) {
      console.warn('[speciesLore] load failed:', error.message);
      return cache || [];
    }
    // AU variants are route-scoped truth — keeping them out of the general
    // canon block is what keeps one route's rules from leaking into another.
    cache = (data || []).filter(r => !r.is_au);
    cacheTime = now;
    return cache;
  } catch (err) {
    console.warn('[speciesLore] load error:', err?.message || err);
    return cache || [];
  }
}

/**
 * Match a character's species string to a lore row.
 *
 * Species on characters are written loosely — "Siren (Atlantic Royalty)",
 * "Vampire/Warlock", "High Fae" — so this matches on the lore row's name,
 * longest name first so "High Fae" wins over "Fae".
 *
 * MATCHES THE DECLARATION, NOT THE BIOGRAPHY (2026-08-12). `character_overrides.
 * species` is not a label — it is authored prose, often 1–2K characters of it,
 * and this function used to run `text.includes(name)` over the whole essay. So
 * a species was assigned by whoever happened to be MENTIONED in a character's
 * backstory. Measured over all 70: 14 characters were handed another species'
 * canon —
 *
 *   ben     "Supernatural — and explicitly NOT a vampire"  → Vampire
 *   zander  Pixie, whose wings "twitch in irritation"      → Witch
 *   ryoto   Dragon, made by a witch in his origin story    → Witch
 *   york    Ali, at a college "where vampires … study"     → Vampire
 *   mr_han_sr  a Vampire, attacked once by a werewolf      → Werewolf
 *   eva/hunter/jenning/maxen_bryers — Werewolves whose canon names the
 *           Ralidanti as the species they are AT WAR WITH → Ralidanti
 *   lachesis  Goddess of Fate                              → Witch
 *
 * — while the block they were given announces itself as "authoritative over
 * reference blurbs, writing samples, and your own instincts" and instructs the
 * model never to borrow another species' concepts. This module's own header
 * describes the bug it was built to fix: a vampire Elder confabulating
 * werewolf pack bonds. It was then handing actual vampires actual werewolf
 * canon.
 *
 * This is the same failure v256 fixed in speciesTraits.js ("herself" matched
 * "elf"; "explicitly NOT a vampire" read as vampire) — the producer changed
 * shape from label to essay and this consumer never learned. It now shares
 * that fix's matcher instead of carrying a second copy: the label only (the
 * declaration before the em-dash), word-START matching, and the negation
 * window that makes "NOT a vampire" stop meaning vampire.
 *
 * Verified against all 70 live species values before shipping: the 37 correct
 * matches are untouched, 7 wrong assignments are dropped, and 7 characters
 * move to the row they should always have had (the four werewolves included).
 */
export async function getSpeciesLoreFor(speciesStrings) {
  const wanted = (Array.isArray(speciesStrings) ? speciesStrings : [speciesStrings])
    .map(s => speciesLabelOf(s).toLowerCase())
    .filter(Boolean);
  if (!wanted.length) return [];
  const all = await loadAll();
  const hits = [];
  const seen = new Set();
  for (const label of wanted) {
    for (const { row, alias } of aliasIndex(all)) {
      if (assertionIndex(label, alias) === -1) continue;
      // Already contributed by an earlier cast member — fall through to the
      // next row, exactly as the pre-2026-08-12 loop did, so a hybrid label
      // can still pick up its second species.
      if (seen.has(row.display_name)) continue;
      seen.add(row.display_name);
      hits.push(row);
      break;
    }
  }
  return hits;
}

/**
 * Every (row, alias) pair, most specific alias first.
 *
 * A display_name is a title, not a match term. Two rows carry a parenthetical
 * — "Fate (Moirai)" and "God (Olympian / Pantheon)" — and no character has
 * ever been written as "Fate (Moirai)", so before 2026-08-12 those two rows
 * matched ZERO of 70 characters. Both hold real authored canon, and the
 * characters they were written for are the ones Haley and Nicole play: the
 * three Moirai, and eight Olympians. Stripping the parenthetical is what makes
 * them reachable.
 *
 * ORDERED BY ALIAS LENGTH, not display_name length, and that ordering is the
 * ruling: "Goddess of Fate (The Cutter)" contains both "fate" and — at a word
 * start, which is how "Goddess" must keep matching "god" — "god". Longest
 * alias first means "fate" (4) is tried before "god" (3), so a Goddess of Fate
 * gets Fate canon rather than Olympian canon. Haley, 2026-08-12. Ties keep the
 * old longest-display_name order so nothing else moves.
 */
export function aliasIndex(rows) {
  const ordered = [...rows].sort(
    (a, b) => String(b.display_name || '').length - String(a.display_name || '').length
  );
  const pairs = [];
  ordered.forEach((row, rank) => {
    const name = String(row.display_name || '').toLowerCase();
    if (!name) return;
    const bare = name.replace(/\s*\([^)]*\)\s*/g, ' ').trim();
    // ALIASES (2026-10-05, audit 4.3): the row is named "Fae" and ~45
    // characters are written as "Fairy", "Fairy Prince", "Spring Fairy"…;
    // "Archangel" never reached Angel, "Demigod" never reached God. The
    // writers keep a comma-separated "Also called" list on the species form
    // and it matches exactly like the name — word-start, negation-aware.
    const extra = String(row.aliases || '').split(/[,;\n]/).map(s => s.trim().toLowerCase()).filter(Boolean);
    const parts = [name, bare, ...name.split('/').map(s => s.trim()),
                   ...bare.split('/').map(s => s.trim()), ...extra];
    // The parenthetical's own contents are match terms too: "Olympian" and
    // "Pantheon" are how a god is actually described.
    for (const m of name.matchAll(/\(([^)]*)\)/g)) {
      for (const piece of m[1].split('/')) parts.push(piece.trim());
    }
    for (const alias of new Set(parts.filter(Boolean))) pairs.push({ row, alias, rank });
  });
  return pairs.sort((a, b) => (b.alias.length - a.alias.length) || (a.rank - b.rank));
}

/**
 * Format matched lore as a CANON block.
 *
 * Deliberately framed as outranking the voice samples. The samples are how a
 * character sounds; they are not a source of world rules, and letting them act
 * as one is exactly how werewolf pack bonds ended up in a vampire's mouth.
 */
export function formatSpeciesLoreForPrompt(rows) {
  if (!rows || rows.length === 0) return '';
  const part = (label, value) =>
    value && String(value).trim() ? `  ${label}: ${String(value).trim()}\n` : '';
  let out = `\n═══ SPECIES CANON — the established truth of this world ═══\nThis is authoritative over reference blurbs, writing samples, and your own instincts about how these species work. The one thing that outranks it is what has ACTUALLY BEEN PLAYED in the RP history you were given — lived story beats established at the table win over these condensed notes. Do not invent rules, hierarchies or customs that are not here, and never borrow another species' concepts (a vampire does not have pack bonds; a werewolf does not have a sire).\n`;
  for (const r of rows) {
    out += `\n${String(r.display_name || 'Unknown').toUpperCase()}\n`;
    out += part('Origin', r.origin);
    out += part('Transformation', r.transformation);
    out += part('Abilities', r.abilities);
    out += part('Weaknesses', r.weaknesses);
    out += part('Hierarchy', r.hierarchy);
    out += part('Social rules', r.social_rules);
    out += part('Concealment', r.concealment_rules);
    out += part('Politics', r.politics);
    out += part('Taboos', r.taboos);
    out += part('Relations with other species', r.relations_other_species);
    out += part('Reproduction', r.reproduction);
    out += part('Lifespan', r.lifespan);
    out += part('Aging', r.aging);
    out += part('Feeding', r.feeding);
    out += part('Death rites', r.death_rites);
    // The truth has layers: what members KNOW is true canon; what outsiders
    // BELIEVE is often wrong, and the gap between them is playable. Never
    // present an outsider belief as fact.
    out += part('What members of this species know (true)', r.insider_knowledge);
    out += part('What outsiders believe (often WRONG — the gap is playable, not fact)', r.outsider_beliefs);
    out += part('Common misconceptions (each false, with the truth)', r.common_misconceptions);
    // Named exceptions OVERRIDE the general rules above for those
    // individuals — and only those individuals. Do not generalise from them.
    out += part('NAMED EXCEPTIONS (override the rules above for these individuals ONLY)', r.exceptions);
    out += part('Other', r.additional_lore);
    for (const [k, v] of Object.entries(r.extra_lore || {})) {
      out += part(k.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase()), v);
    }
  }
  return out;
}

/** Convenience: species strings → formatted prompt block (empty on any failure). */
export async function buildSpeciesLoreSection(speciesStrings) {
  try {
    return formatSpeciesLoreForPrompt(await getSpeciesLoreFor(speciesStrings));
  } catch {
    return '';
  }
}
